import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import TextInputHookFormDisabled from "../../../../components/TextInputHookFormDisabled";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  officeTypeAddressNotRequired,
  officesOptions,
  rowForEditOfficeSettings,
  officeAddState,
  officeEditState,
  workFromHome,
  onchangeOfc,
} from "../atoms/atoms";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { useStates } from "../../hooks/useStates";
import { accountAtom } from "modules/Login/atoms/AccountAtom";
import { getStateCode } from "utils/mexicoStateCodeMapping";
import { useGetAddressLineType } from "../api/getAddressLineType";
import { toastError } from "components/toastNotification";

type Library = (
  | "places"
  | "drawing"
  | "geometry"
  | "localContext"
  | "visualization"
)[];
const libraries: Library = ["places"];
export const OfficeAddress = ({ disableFields }) => {
  const formMethods = useFormContext();
  const officeOpt = useRecoilValue(officesOptions);
  const officeTypeAddressNotRequire = useRecoilValue(
    officeTypeAddressNotRequired
  );
  const officeName = formMethods
    .watch("officeName")
    ?.toLowerCase()
    ?.includes("work from home");

  const user = useRecoilValue(accountAtom);

  const [onChangeOffice] = useRecoilState(onchangeOfc);
  const [rowForEdit] = useRecoilState(rowForEditOfficeSettings);
  const [isAddState] = useRecoilState(officeAddState);
  const [isEditState] = useRecoilState(officeEditState);
  const [isVerified, setVerifyLoading] = useState(false);
  const [validWorkFrom] = useRecoilState(workFromHome);
  const [searchBillingBox, setBillingSearchBox] = useState<any>(null);
  const [suiteOptions, setSuiteOptions] = useState<any[]>([]);

  const suiteDetails = formMethods.watch("addressLineTypeId");
  const selected = suiteOptions.find((x) => x.value === suiteDetails);
  const label = selected?.label;

  const normalizedSuite = String(suiteDetails ?? "").trim();
  const isNone = label === "NONE";
  const isEmpty = !normalizedSuite;

  const { data: addressLineTypeData, isLoading: isAddressLineTypeFetching } =
    useGetAddressLineType();

  const isSuiteNumberRequired =
    normalizedSuite !== "" && normalizedSuite !== "NONE";

  useEffect(() => {
    if (isNone || !suiteDetails) {
      formMethods.setValue("suiteNumber", "");
    }
  }, [suiteDetails]);

  useEffect(() => {
    try {
      if (isAddressLineTypeFetching) return;

      const mapped = addressLineTypeData?.map((x: any) => ({
        label: x.addressLineTypeName,
        value: x.addressLineTypeId,
      }));

      setSuiteOptions(mapped ?? []);
    } catch (err) {
      toastError("Failed to load address line types", err);
    }
  }, [addressLineTypeData, isAddressLineTypeFetching]);

  const { data: states } = useStates();
  const allStatesOpt = states?.map((x) => {
    return {
      label: x.state,
      description: x.description,
      value: x.state?.toString(),
    };
  });

  const apiKey = user?.googleApiKey ?? "";
  const timeZoneKey = user?.googleTimezoneApiKey ?? "";

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries,
  });

  function getOfficeTimeZone(url_Request) {
    var xhr = new XMLHttpRequest(); // create new XMLHttpRequest2 object
    return new Promise((resolve, reject) => {
      xhr.open("GET", url_Request); // open GET request
      xhr.onload = function () {
        if (xhr.status === 200) {
          // if Ajax request successful
          var output = JSON.parse(xhr.responseText);
          if (output.status === "OK") {
            resolve(output);
          }
        }
      };
      xhr.send();
    });
  }

  function getOfficeLocation(url_Request) {
    //  setVerifyLoading(true);
    var xhr = new XMLHttpRequest(); // create new XMLHttpRequest2 object
    return new Promise((resolve, reject) => {
      xhr.open("GET", url_Request); // open GET request
      xhr.onload = function () {
        if (xhr.status === 200) {
          // if Ajax request successful
          var output = JSON.parse(xhr.responseText);
          if (output.status === "OK") {
            resolve(output);
          }
        }
      };
      xhr.send();
    });
  }

  const watchedOfficeName = formMethods.watch("officeName");
  useEffect(() => {
    if ((validWorkFrom && isAddState) || (officeName && isAddState)) {
      formMethods.setValue("address", "7711 Center Ave St 200");
      formMethods.setValue("zipCode", null);
      formMethods.setValue("city", null);
      formMethods.setValue("state", null);
      formMethods.setValue("stateCode", null);
      formMethods.setValue("shippingAddressLatitude", null);
      formMethods.setValue("shippingAddressLongitude", null);
      formMethods.setValue("geolocation", null);
      formMethods.setValue("shippingLocation", "");
      formMethods.setValue("locLattitude", null);
      formMethods.setValue("locLongitude", null);
      formMethods.setValue("shippingTimeZone", null);
      formMethods.setValue("shippingDaylightSavings", false);
      formMethods.setValue("shippingCounty", null);
      formMethods.setValue("shippingLastVerified", null);
    } else if (
      isEditState &&
      rowForEdit?.officeName?.toLowerCase()?.includes("work from home") &&
      onChangeOffice &&
      !validWorkFrom
    ) {
      formMethods.setValue("shippingLocation", "");
      formMethods.setValue("geolocation", null);
      formMethods.setValue("locLattitude", null);
      formMethods.setValue("locLongitude", null);
      formMethods.setValue("shippingAddressLatitude", null);
      formMethods.setValue("shippingAddressLongitude", null);
      formMethods.setValue("shippingTimeZone", null);
      formMethods.setValue("shippingDaylightSavings", false);
      formMethods.setValue("shippingCounty", null);
      formMethods.setValue("shippingLastVerified", null);
      formMethods.setValue("address", null);
      formMethods.setValue("zipCode", null);
      formMethods.setValue("city", null);
      formMethods.setValue("state", null);
      formMethods.setValue("stateCode", null);
    } else if (
      isEditState &&
      !rowForEdit?.officeName?.toLowerCase()?.includes("work from home") &&
      onChangeOffice &&
      validWorkFrom
    ) {
      formMethods.setValue("shippingLocation", "");
      formMethods.setValue("geolocation", null);
      formMethods.setValue("locLattitude", null);
      formMethods.setValue("locLongitude", null);
      formMethods.setValue("shippingAddressLatitude", null);
      formMethods.setValue("shippingAddressLongitude", null);
      formMethods.setValue("shippingTimeZone", null);
      formMethods.setValue("shippingDaylightSavings", false);
      formMethods.setValue("shippingCounty", null);
      formMethods.setValue("shippingLastVerified", null);
      formMethods.setValue("address", null);
      formMethods.setValue("zipCode", null);
      formMethods.setValue("city", null);
      formMethods.setValue("state", null);
      formMethods.setValue("stateCode", null);
    }
  }, [validWorkFrom, formMethods.watch("officeName")]);

  // URL builder for google maps
  const buildGoogleApiUrl = (
    baseUrl: string,
    params: Record<string, string | number>
  ): string => {
    const url = new URL(baseUrl);
    for (const [key, value] of Object.entries(params)) {
      if (value != null) {
        url.searchParams.set(key, value.toString());
      }
    }
    return url.toString();
  };

  // Helper function to round latitude and longitude
  const roundGeo = (value?: number | null): number | null => {
    if (value == null) return null;
    const rounded = Number(value.toFixed(8)); // round to 8 decimal places
    const str = rounded.toString();
    return str.length > 16 ? Number(str.slice(0, 16)) : rounded; // enforce max 16 characters
  };

  const [city, zipCode, state] = useWatch({
    control: formMethods.control,
    name: ["city", "zipCode", "state"],
  });

  useEffect(() => {
    if (!validWorkFrom && !officeName) return;

    if (!city || !zipCode || !state) return;

    // Build Geocode API URL
    const URLGeo = buildGoogleApiUrl(
      "https://maps.googleapis.com/maps/api/geocode/json",
      {
        address: `${zipCode},${city},${state}`,
        key: timeZoneKey,
      }
    );

    // Fetch geocode information
    getOfficeLocation(URLGeo).then((res: any) => {
      const result = res?.results?.[0];
      if (!result?.geometry?.location) return;

      const rawLatitude = result.geometry.location.lat;
      const rawLongitude = result.geometry.location.lng;

      const locLatitude = roundGeo(rawLatitude);
      const locLongitude = roundGeo(rawLongitude);

      const geoDisplay = `${locLatitude}, ${locLongitude}`; // for form display
      const geoApi = `${locLatitude},${locLongitude}`; // for API (no space)

      formMethods.setValue("shippingAddressLatitude", locLatitude);
      formMethods.setValue("shippingAddressLongitude", locLongitude);
      formMethods.setValue("geolocation", geoDisplay);
      formMethods.setValue("shippingLocation", result.formatted_address);
      formMethods.setValue("locLattitude", locLatitude);
      formMethods.setValue("locLongitude", locLongitude);

      //TimeZone API
      const timestamp =
        Math.floor(Date.now() / 1000) + new Date().getTimezoneOffset() * 60;
      const urlRequest = buildGoogleApiUrl(
        "https://maps.googleapis.com/maps/api/timezone/json",
        {
          location: geoApi,
          timestamp: timestamp,
          key: timeZoneKey,
        }
      );

      getOfficeTimeZone(urlRequest).then((res: any) => {
        const parts = res?.timeZoneName?.split(" ") || [];
        const timeZone = parts[0] + " " + parts[2];
        formMethods.setValue("shippingTimeZone", res.timeZoneName);
        formMethods.setValue(
          "shippingDaylightSavings",
          Boolean(res.dstOffset && res.dstOffset > 0)
        );
      });
    });
  }, [watchedOfficeName, city, zipCode, state, officeName, validWorkFrom]);

  const autocompleteRef = useRef<any>(null);

  // const isLoaded = true;
  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onBillingLoad = (ref) => {
    setBillingSearchBox(ref);
  };

  const OnPlacesChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current?.getPlace();
      if (!place) return;

      setVerifyLoading(true);

      const rawLatitude = place.geometry?.location?.lat();
      const rawLongitude = place.geometry?.location?.lng();

      const locLatitude = roundGeo(rawLatitude);
      const locLongitude = roundGeo(rawLongitude);

      const geoDisplay = `${locLatitude}, ${locLongitude}`; // for form display
      const geoApi = `${locLatitude},${locLongitude}`; // for API (no space)

      formMethods.setValue("shippingAddressLatitude", locLatitude);
      formMethods.setValue("shippingAddressLongitude", locLongitude);
      formMethods.setValue("geolocation", geoDisplay);
      formMethods.setValue("shippingLocation", place.formatted_address);
      formMethods.setValue("locLattitude", locLatitude);
      formMethods.setValue("locLongitude", locLongitude);
      //TimeZone
      const timestamp =
        Math.floor(Date.now() / 1000) + new Date().getTimezoneOffset() * 60;
      const urlRequest = buildGoogleApiUrl(
        "https://maps.googleapis.com/maps/api/timezone/json",
        {
          location: geoApi,
          timestamp: timestamp,
          key: timeZoneKey,
        }
      );

      getOfficeLocation(urlRequest).then((res: any) => {
        if (res?.timeZoneName) {
          const parts = res.timeZoneName.split(" ");
          const timeZone = parts[0] + " " + parts[2];
          formMethods.setValue("shippingTimeZone", timeZone);
          formMethods.setValue("shippingDaylightSavings", res.dstOffset > 0);
        }
      });

      //Address Components Helper
      const getComponent = (type: string) =>
        place.address_components?.find((c: any) => c.types?.includes(type));
      if (place.address_components) {
        const country = getComponent("country");
        const state = getComponent("administrative_area_level_1");
        const county = getComponent("administrative_area_level_2");
        const city = getComponent("locality");
        const suiteNumber = getComponent("subpremise");
        const zipCode = getComponent("postal_code");
        const streetNumber = getComponent("street_number");
        const street = getComponent("route");
        const neighborhood = getComponent("neighborhood");

        let streetNumberLongname = streetNumber?.long_name ?? " ";
        let streetShortName = street?.short_name ?? " ";
        setTimeout(() => {
          formMethods.setValue(
            "shippingLastVerified",
            new Date().toLocaleString()
          );
          formMethods.setValue("countryCode", country?.short_name);
          formMethods.setValue("country", country?.long_name);

          //State code mapping for Mexico
          const stateCode = getStateCode(state?.long_name, state?.short_name);
          formMethods.setValue("stateCode", stateCode);
          formMethods.setValue("state", state?.long_name);

          if (!officeName) {
            clearLegalEntity(state?.short_name);
            clearBrand(state?.short_name);
            clearAcquisition(state?.short_name);
          }

          formMethods.setValue("shippingCounty", county?.short_name);
          formMethods.setValue(
            "city",
            neighborhood?.short_name === "Encino" ? "Encino" : city?.short_name
          );
          formMethods.setValue("zipCode", zipCode?.short_name);
          formMethods.setValue(
            "address",
            `${streetNumberLongname} ${streetShortName}`
          );
          setVerifyLoading(false);
        }, 500);
      }
    }
  };

  const onBillingPlacesChanges = () => {
    const place = searchBillingBox?.getPlace();
    const lat = place?.geometry?.location?.lat();
    const long = place?.geometry?.location?.lng();
    formMethods.setValue("billingAddressLatitude", lat);
    formMethods.setValue("billingAddressLongitude", long);
    formMethods.setValue("billingLocation", place?.formatted_address);

    if (place?.address_components?.length > 1) {
      let components = place?.address_components;
      let country = components?.find((item: any) =>
        item?.types?.includes("country")
      );
      let state = components?.find((item: any) =>
        item?.types?.includes("administrative_area_level_1")
      );
      let city = components?.find((item: any) =>
        item?.types?.includes("locality")
      );
      let zipCode = components?.find((item: any) =>
        item?.types?.includes("postal_code")
      );
      let streetNumber = components?.find((item: any) =>
        item?.types?.includes("street_number")
      );
      let street = components?.find((item: any) =>
        item?.types?.includes("route")
      );
      let neighborhood = components?.find((item: any) =>
        item?.types?.includes("neighborhood")
      );

      formMethods.setValue("billingStateCode", state?.short_name);
      formMethods.setValue("billingState", state?.long_name);
      formMethods.setValue(
        "billingCity",
        neighborhood?.short_name === "Encino" ? "Encino" : city?.short_name
      );
      formMethods.setValue("billingCountryCode", country?.short_name);
      formMethods.setValue("billingCountry", country?.long_name);
      formMethods.setValue("billingZipCode", zipCode?.short_name);
      formMethods.setValue(
        "billingAddress",
        streetNumber?.long_name + " " + street?.short_name
      );
    } else {
      let components = place?.address_components;
      let country = components?.find((item: any) =>
        item?.types?.includes("country")
      );

      formMethods.setValue("billingAddress", country.long_name);
      formMethods.setValue("billingState", null);
      formMethods.setValue("billingStateCode", null);
      formMethods.setValue("billingCity", null);
      formMethods.setValue("billingZipCode", null);
      formMethods.setValue("billingCountryCode", country.short_name);
      formMethods.setValue("billingCountry", country.long_name);
    }
  };

  const clearBillingAddress = () => {
    formMethods.setValue("billingAddressLatitude", null);
    formMethods.setValue("billingAddressLongitude", null);
    formMethods.setValue("billingLocation", null);
    formMethods.setValue("billingAddress", null);
    formMethods.setValue("billingState", null);
    formMethods.setValue("billingStateCode", null);
    formMethods.setValue("billingCity", null);
    formMethods.setValue("billingZipCode", null);
    formMethods.setValue("billingCountryCode", null);
    formMethods.setValue("billingCountry", null);
  };

  const [oldLegalEntity, setOldLegalEntity] = useState("");
  const clearLegalEntity = (stateCode) => {
    if (isEditState && stateCode !== rowForEdit?.stateCode) {
      formMethods.setValue("entityId", null);
    }
    if (isAddState && oldLegalEntity !== stateCode) {
      formMethods.setValue("entityId", null);
      setOldLegalEntity(stateCode);
    }
  };

  const [oldBrand, setOldBrand] = useState("");
  const clearBrand = (brand) => {
    if (isEditState && brand !== rowForEdit?.stateCode) {
      formMethods.setValue("brandId", null);
    }
    if (isAddState && oldBrand !== brand) {
      formMethods.setValue("brandId", null);
      setOldBrand(brand);
    }
  };

  const [oldAcquisition, setOldAcquisition] = useState("");
  const clearAcquisition = (acquisition) => {
    if (isEditState && acquisition !== rowForEdit?.stateCode) {
      formMethods.setValue("acquisitionId", undefined);
      formMethods.setValue("acquisitionName", null);
    }
    if (isAddState && oldAcquisition !== acquisition) {
      formMethods.setValue("acquisitionId", undefined);
      formMethods.setValue("acquisitionName", null);
      setOldAcquisition(acquisition);
    }
  };

  return (
    <div className="m-2 bg-white box-shadow rounded-lg h-full p-1 pb-4">
      <div className="text-quinary font-bold font-sans py-2 px-4">
        Address Information
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 text-xs px-4 gap-12">
        <div className="space-y-1">
          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-2">
            {!validWorkFrom && !officeName && (
              <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                Physical Address:
              </label>
            )}
            {!validWorkFrom && !officeName && isLoaded && (
              <div className="col-span-2">
                <Controller
                  control={formMethods.control}
                  name="shippingLocation"
                  rules={{
                    required: formMethods.watch("address") ? false : true,
                  }}
                  render={({ field }) => (
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={OnPlacesChanged}
                      restrictions={{
                        country: user.isMexico
                          ? user.isFranchise || user.isNonFranchise
                            ? ["mx", "us"]
                            : ["mx"]
                          : ["us"],
                      }}
                    >
                      <input
                        {...field}
                        type="text"
                        placeholder="Enter Location"
                        className={`${
                          formMethods.formState.errors.address ||
                          formMethods.formState.errors.city ||
                          formMethods.formState.errors.stateCode ||
                          formMethods.formState.errors.zipCode ||
                          formMethods.formState.errors.country
                            ? "border-2 border-red-500"
                            : "border border-gray-300"
                        }  focus:ring-septenary focus:border-septenar block w-full text-login bg-white bg-clip-padding leading-normal outline-none block w-full 
            px-1 py-1 rounded-sm`}
                        disabled={isVerified || disableFields}
                      />
                    </Autocomplete>
                  )}
                />
                {isVerified && (
                  <span className="text-red-500 italic text-center">
                    Verifying Address...
                  </span>
                )}
                {(formMethods.formState.errors.address ||
                  formMethods.formState.errors.city ||
                  formMethods.formState.errors.stateCode ||
                  formMethods.formState.errors.zipCode ||
                  formMethods.formState.errors.country) && (
                  <span className="text-red-500 italic">
                    Address fields must be complete
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 items-start gap-2">
            <span className="flex space-x-1 justify-start expandedLabel fontExpand pt-1">
              Address Line 2:
            </span>
            <div className="col-span-2 grid grid-cols-2 gap-2 items-start">
              <Controller
                control={formMethods.control}
                name="addressLineTypeId"
                render={({ field }) => (
                  <SelectHookFormSingle
                    secondDiv="grid md:grid-cols-2 items-center gap-2"
                    field={field}
                    value={field.value}
                    options={suiteOptions}
                    onChange={(e) => {
                      const value = e?.value ?? null;
                      field.onChange(value);

                      if (value === "NONE") {
                        formMethods.setValue("suiteNumber", "");
                      }
                    }}
                    isLoading={isAddressLineTypeFetching}
                  />
                )}
              />

              <div className="relative w-full flex items-center">
                <div className="w-full">
                  <input
                    {...formMethods.register("suiteNumber", {
                      required:
                        isSuiteNumberRequired && !isNone
                          ? "Suite number is required"
                          : false,
                    })}
                    type="text"
                    disabled={disableFields || isNone || isEmpty}
                    className={`w-full text-xs rounded-sm px-1 py-1 bg-white leading-normal outline-none focus:ring-septenary focus:border-septenary disabled:opacity-50 disabled:bg-gray-100 ${
                      formMethods.formState.errors.suiteNumber
                        ? "border border-red-500"
                        : "border border-gray-300"
                    }`}
                  />
                </div>
                {isSuiteNumberRequired && !isNone && (
                  <span className="text-red-500 font-bold text-base">*</span>
                )}
              </div>
            </div>
          </div>

          {!validWorkFrom && !officeName && (
            <TextInputHookFormDisabled
              name="address"
              label="Address"
              type="text"
              required={
                !officeTypeAddressNotRequire.includes(
                  formMethods.watch("officeType")
                )
              }
              register={formMethods.register("address", {
                required: {
                  value: !officeTypeAddressNotRequire.includes(
                    formMethods.watch("officeType")
                  ),
                  message: "Required Field",
                },
              })}
            />
          )}
          {!validWorkFrom && !officeName && formMethods.watch("address") && (
            <>
              <TextInputHookFormDisabled
                name="city"
                label="City"
                type="text"
                required={true}
                register={formMethods.register("city", {})}
              />

              <TextInputHookFormDisabled
                name="stateCode"
                label="State"
                type="text"
                required={true}
                register={formMethods.register("state", {})}
              />
              <TextInputHookFormDisabled
                name="zipCode"
                label="ZIP Code"
                type="text"
                required={true}
                register={formMethods.register("zipCode", {})}
              />
              <TextInputHookFormDisabled
                name="country"
                label="Country"
                type="text"
                required={true}
                register={formMethods.register("country", {})}
              />
            </>
          )}
          {(validWorkFrom || officeName) && (
            <>
              <TextInputLabelHookForm
                name="address"
                label="Address"
                required={true}
                register={formMethods.register("address", {
                  required: {
                    value:
                      !officeTypeAddressNotRequire.includes(
                        formMethods.watch("officeType")
                      ) ||
                      validWorkFrom ||
                      officeName,
                    message: "Required Field",
                  },
                })}
                onChange={(e) => {
                  formMethods.setValue("address", e.target.value);
                }}
              />
              <TextInputLabelHookForm
                name="city"
                label="City"
                required={true}
                register={formMethods.register("city", {
                  required: {
                    value: true,
                    message: "Required Field",
                  },
                })}
                className={
                  formMethods.formState.errors.city &&
                  "border-2 border-red-500 col-span-2 "
                }
                onChange={(e) => {
                  formMethods.setValue("city", e.target.value);
                }}
              />
              <Controller
                control={formMethods.control}
                rules={{
                  required: true,
                }}
                name="stateCode"
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    value={field.value}
                    options={allStatesOpt}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                      formMethods.setValue("stateCode", e.value);
                      formMethods.setValue("state", e.description);
                      clearLegalEntity(e.value);
                      clearBrand(e.value);
                      clearAcquisition(e.value);
                    }}
                    label="State"
                    required={true}
                    className={
                      formMethods.formState.errors.stateCode &&
                      "border-2 border-red-500 col-span-2 "
                    }
                  />
                )}
              />
              <TextInputLabelHookForm
                name="zipCode"
                label="ZIP Code"
                type="text"
                required={true}
                register={formMethods.register("zipCode", {
                  required: {
                    value: true,
                    message: "Required Field",
                  },
                })}
                className={
                  formMethods.formState.errors.zipCode &&
                  "border-2 border-red-500 col-span-2 "
                }
                onChange={(e) => {
                  formMethods.setValue("zipCode", e.target.value);
                }}
              />
            </>
          )}
          <Controller
            control={formMethods.control}
            name="nextClosestOffice"
            render={({ field }) => (
              <SelectHookFormSingle
                field={field}
                onChange={(e) => {
                  field.onChange(e ? e.value : null);
                }}
                value={field.value}
                options={officeOpt.filter(
                  (office: any) =>
                    office?.value?.toLowerCase() !==
                    formMethods.watch("officeCode")?.toLowerCase()
                )}
                label="Next Closest Office"
                isDisabled={disableFields}
              />
            )}
          />
        </div>

        <div className="space-y-1">
          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-2">
            <label className="flex space-x-1 justify-start expandedLabel fontExpand">
              Billing Address:
            </label>

            {isLoaded && (
              <div className="col-span-2">
                <Controller
                  control={formMethods.control}
                  name="billingLocation"
                  render={({ field }) => (
                    <Autocomplete
                      onLoad={onBillingLoad}
                      onPlaceChanged={onBillingPlacesChanges}
                      restrictions={{ country: "us" }}
                    >
                      <input
                        {...field}
                        type="text"
                        placeholder="Enter Location"
                        className="border border-gray-300 focus:ring-septenary focus:border-septenar block w-full text-login bg-white bg-clip-padding leading-normal outline-none block w-full 
            px-1 py-1 rounded-sm"
                        onChange={(e: any) => {
                          field.onChange(e.target.value);
                          if (!e.target.value) {
                            clearBillingAddress();
                          }
                        }}
                        disabled={disableFields}
                      />
                    </Autocomplete>
                  )}
                />
              </div>
            )}
          </div>
          <TextInputHookFormDisabled
            name="billingAddress"
            label="Address"
            type="text"
            register={formMethods.register("billingAddress", {})}
          />
          {formMethods.watch("billingAddress") && (
            <>
              <TextInputHookFormDisabled
                name="billingCity"
                label="City"
                type="text"
                register={formMethods.register("billingCity", {})}
              />
              <TextInputHookFormDisabled
                name="billingState"
                label="State"
                type="text"
                register={formMethods.register("billingState", {})}
              />
              <TextInputHookFormDisabled
                name="billingZipCode"
                label="ZIP Code"
                type="text"
                register={formMethods.register("billingZipCode", {})}
              />
              <TextInputHookFormDisabled
                name="billingCountry"
                label="Country"
                type="text"
                register={formMethods.register("billingCountry", {})}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfficeAddress;
